import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { ServerInferRequest } from "@ts-rest/core";
import {
    CatalogItem,
    CatalogItemArchivedFilter,
    CatalogItemSort,
} from "@common/types/catalogItem";
import { InvoiceSource } from "@common/types/invoice";
import { catalogItemContract } from "@common/contracts/catalogItem.contract";
import {
    BulkSetCatalogItemsActiveBody,
    BulkSetCatalogItemsActiveResult,
    ListCatalogItemsQuery,
} from "@common/schemas/domains/catalogItem/catalogItem.schema";
import { useScope } from "@state/scopeContext.lib";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { SUPPRESS_ERROR_TOAST_HEADER } from "@lib/backendClient";
import { queryClient } from "../queryClient";

const catalogItemContractClient = getBackendAPIClient(catalogItemContract);

interface ListCatalogItemsParams {
    search?: string;
    entityIds?: number[];
    archivedFilter?: CatalogItemArchivedFilter;
    sort?: CatalogItemSort;
    sources?: InvoiceSource[];
    classIds?: string[];
    classFilterType?: ListCatalogItemsQuery["classFilterType"];
    incomeAccountIds?: string[];
    receivablesAccountIds?: string[];
    // Both omitted = unpaginated (existing behaviour for e.g. the invoice line
    // item catalog item combobox). Pass both to page through results.
    page?: number;
    limit?: number;
    enabled?: boolean;
}

interface CatalogItemsResult {
    data: CatalogItem[];
    total: number;
    pageCount: number;
}

const getCatalogItemsQueryKey = (
    workspaceId: string,
    params: Omit<ListCatalogItemsParams, "enabled"> = {},
) => ["catalogItems", workspaceId, params];

export function invalidateCatalogItemsQueries(workspaceId: string) {
    queryClient.invalidateQueries({ queryKey: ["catalogItems", workspaceId] });
}

// Opt-in per call: skip the axios interceptor's auto error toast so a caller
// (e.g. CatalogItemFormModal) can surface its own contextual message.
export function suppressErrorToastHeaders(
    suppressErrorToast: boolean | undefined,
) {
    return suppressErrorToast
        ? { extraHeaders: { [SUPPRESS_ERROR_TOAST_HEADER]: "true" } }
        : {};
}

const EMPTY_CATALOG_ITEMS_RESULT: CatalogItemsResult = {
    data: [],
    total: 0,
    pageCount: 1,
};

export function useCatalogItems({
    search,
    entityIds,
    archivedFilter,
    sort,
    sources,
    classIds,
    classFilterType,
    incomeAccountIds,
    receivablesAccountIds,
    page,
    limit,
    enabled = true,
}: ListCatalogItemsParams = {}) {
    const { workspaceId } = useScope();

    const query = useQuery({
        queryKey: workspaceId
            ? getCatalogItemsQueryKey(workspaceId, {
                  search,
                  entityIds,
                  archivedFilter,
                  sort,
                  sources,
                  classIds,
                  classFilterType,
                  incomeAccountIds,
                  receivablesAccountIds,
                  page,
                  limit,
              })
            : ["catalogItems", "disabled"],
        queryFn: async (): Promise<CatalogItemsResult> => {
            if (!workspaceId) {
                return EMPTY_CATALOG_ITEMS_RESULT;
            }
            const result = await catalogItemContractClient.listCatalogItems({
                params: { workspaceId },
                query: {
                    search,
                    entityIds,
                    archivedFilter,
                    sort,
                    sources,
                    classIds,
                    classFilterType,
                    incomeAccountIds,
                    receivablesAccountIds,
                    page,
                    limit,
                },
            });
            return result.body;
        },
        enabled: enabled && !!workspaceId,
        staleTime: 1000 * 60,
        // Keep the previous page (and footer totals) rendered while the next
        // page loads instead of flashing an empty table and dropping the
        // pagination controls, matching other paginated lists (class.api.ts).
        placeholderData: keepPreviousData,
    });

    return {
        // Stable fallback reference: a fresh [] per render forces consumers
        // (e.g. Base UI Combobox's store sync) into infinite update loops.
        items: query.data?.data ?? EMPTY_CATALOG_ITEMS_RESULT.data,
        total: query.data?.total ?? 0,
        pageCount: query.data?.pageCount ?? 1,
        isLoading: query.isLoading,
        isFetching: query.isFetching,
        isPlaceholderData: query.isPlaceholderData,
    };
}

type CreateCatalogItemBody = ServerInferRequest<
    typeof catalogItemContract.createCatalogItem
>["body"];
type UpdateCatalogItemBody = ServerInferRequest<
    typeof catalogItemContract.updateCatalogItem
>["body"];

export function useCreateCatalogItem() {
    const { workspaceId } = useScope();
    return useMutation({
        mutationFn: async ({
            body,
            suppressErrorToast,
        }: {
            body: CreateCatalogItemBody;
            suppressErrorToast?: boolean;
        }) => {
            if (!workspaceId) {
                throw new Error("Expecting active workspace");
            }
            const result = await catalogItemContractClient.createCatalogItem({
                params: { workspaceId },
                body,
                ...suppressErrorToastHeaders(suppressErrorToast),
            });
            return result.body;
        },
        onSuccess: () => {
            if (workspaceId) {
                invalidateCatalogItemsQueries(workspaceId);
            }
        },
    });
}

export function useUpdateCatalogItem() {
    const { workspaceId } = useScope();
    return useMutation({
        mutationFn: async ({
            catalogItemId,
            body,
            suppressErrorToast,
        }: {
            catalogItemId: string;
            body: UpdateCatalogItemBody;
            suppressErrorToast?: boolean;
        }) => {
            if (!workspaceId) {
                throw new Error("Expecting active workspace");
            }
            const result = await catalogItemContractClient.updateCatalogItem({
                params: { workspaceId, catalogItemId },
                body,
                ...suppressErrorToastHeaders(suppressErrorToast),
            });
            return result.body.item;
        },
        onSuccess: () => {
            if (workspaceId) {
                invalidateCatalogItemsQueries(workspaceId);
            }
        },
    });
}

export interface BulkUpdateCatalogItemsResult {
    succeededIds: string[];
    failedIds: string[];
}

export function useBulkUpdateCatalogItems() {
    const { workspaceId } = useScope();
    return useMutation({
        mutationFn: async ({
            catalogItemIds,
            body,
        }: {
            catalogItemIds: string[];
            body: UpdateCatalogItemBody;
        }): Promise<BulkUpdateCatalogItemsResult> => {
            if (!workspaceId) {
                throw new Error("Expecting active workspace");
            }
            const results = await Promise.allSettled(
                catalogItemIds.map((catalogItemId) =>
                    catalogItemContractClient.updateCatalogItem({
                        params: { workspaceId, catalogItemId },
                        body,
                        ...suppressErrorToastHeaders(true),
                    }),
                ),
            );
            return {
                succeededIds: catalogItemIds.filter(
                    (_, index) => results[index].status === "fulfilled",
                ),
                failedIds: catalogItemIds.filter(
                    (_, index) => results[index].status === "rejected",
                ),
            };
        },
        onSettled: () => {
            if (workspaceId) {
                invalidateCatalogItemsQueries(workspaceId);
            }
        },
    });
}

export function useBulkSetCatalogItemsActive() {
    const { workspaceId } = useScope();
    return useMutation({
        mutationFn: async (
            body: BulkSetCatalogItemsActiveBody,
        ): Promise<BulkSetCatalogItemsActiveResult> => {
            if (!workspaceId) {
                throw new Error("Expecting active workspace");
            }
            const result =
                await catalogItemContractClient.bulkSetCatalogItemsActive({
                    params: { workspaceId },
                    body,
                    ...suppressErrorToastHeaders(true),
                });
            return result.body;
        },
        onSettled: () => {
            if (workspaceId) {
                invalidateCatalogItemsQueries(workspaceId);
            }
        },
    });
}

export function useDeleteCatalogItem() {
    const { workspaceId } = useScope();
    return useMutation({
        mutationFn: async ({
            catalogItemId,
            suppressErrorToast,
        }: {
            catalogItemId: string;
            suppressErrorToast?: boolean;
        }) => {
            if (!workspaceId) {
                throw new Error("Expecting active workspace");
            }
            await catalogItemContractClient.deleteCatalogItem({
                params: { workspaceId, catalogItemId },
                ...suppressErrorToastHeaders(suppressErrorToast),
            });
        },
        onSuccess: () => {
            if (workspaceId) {
                invalidateCatalogItemsQueries(workspaceId);
            }
        },
    });
}

export function useDuplicateCatalogItem() {
    const { workspaceId } = useScope();
    return useMutation({
        mutationFn: async (catalogItemId: string) => {
            if (!workspaceId) {
                throw new Error("Expecting active workspace");
            }
            const result = await catalogItemContractClient.duplicateCatalogItem(
                {
                    params: { workspaceId, catalogItemId },
                    body: {},
                },
            );
            return result.body.item;
        },
        onSuccess: () => {
            if (workspaceId) {
                invalidateCatalogItemsQueries(workspaceId);
            }
        },
    });
}
